/**
 * English comment normalized.
 * fachada sobre {@link ./internal/upp-konva-floor-plan-model.UppKonvaFloorPlanModel} y reexportaciones
 * English comment normalized.
 */

import type { UppFloorPlanExportPngOptions } from './export/upp-floor-plan-export-png';
import { UppKonvaFloorPlanModel } from './internal/upp-konva-floor-plan-model';
import { konvaShapeByUuidFromModel } from './internal/upp-konva-model-resolve-shape';
import { floorPlanElementUuid } from './internal/floor-plan-uuid';
import type { UppKonvaAttention } from '../model/upp-konva-types';
import type { UppKonvaAreaSource } from '../model/upp-konva-source';
import type { UppKonvaFloorPlanGroup } from './presentation/upp-konva-floor-plan-group';
import type { UppFloorPlanPreferences } from './plan/floor-plan-preferences';
import { stageClearSelection } from './internal/upp-konva-stage-host-bridge';
import { stageSelectOnly } from './internal/upp-konva-stage-host-bridge';

/** English comment normalized. */
export type UppKonvaStageLiveArea = UppKonvaAreaSource & {
  onGrouped?: (group: UppKonvaFloorPlanGroup | null) => void;
};

/**
 * English comment normalized.
 * sin que el model importe utilidades internas del lienzo.
 */
export class UppKonvaStage {
  private readonly _floorPlan: UppKonvaFloorPlanModel;

  constructor(areaSource: UppKonvaAreaSource, preferences?: Partial<UppFloorPlanPreferences> | null) {
    this._floorPlan = new UppKonvaFloorPlanModel(areaSource, preferences ?? undefined);
  }

  /**
   * Misma instancia que recibe **`[model]`** en **`upp-konva-floor-plan`** (registro **`uppKonvaSceneModelHost`**).
   */
  get floorPlanModel(): UppKonvaFloorPlanModel {
    return this._floorPlan;
  }

  get mode(): 'VIEW' | 'EDIT' {
    return this._floorPlan.mode;
  }

  set mode(value: 'VIEW' | 'EDIT') {
    this._floorPlan.mode = value;
  }

  get theme(): UppKonvaFloorPlanModel['theme'] {
    return this._floorPlan.theme;
  }

  set theme(value: UppKonvaFloorPlanModel['theme']) {
    this._floorPlan.theme = value;
  }

  get area(): UppKonvaFloorPlanModel['area'] {
    return this._floorPlan.area;
  }

  set area(value: UppKonvaFloorPlanModel['area']) {
    this._floorPlan.area = value;
  }

  get presentationEpoch(): number {
    return this._floorPlan.presentationEpoch;
  }

  get shapes(): UppKonvaFloorPlanModel['shapes'] {
    return this._floorPlan.shapes;
  }

  doRefresh(): void {
    this._floorPlan.doRefresh();
  }

  /**
   * Merges framing preferences onto the live floor-plan model (margin, zoom, …).
   */
  setPresentationPreferences(partial: Partial<UppFloorPlanPreferences> | null | undefined): void {
    this._floorPlan.setPresentationPreferences(partial);
  }

  /**
   * English comment normalized.
   */
  clearHostSelection(): void {
    stageClearSelection(this._floorPlan);
  }

  /**
   * Replaces host selection with the provided scene UUIDs.
   */
  selectHostOnly(elementUuids: readonly string[]): void {
    stageSelectOnly(this._floorPlan, elementUuids);
  }

  doDestroy(): void {
    this._floorPlan.doDestroy();
  }

  applyPresentationFromPublicShape(shape: { id: string; kind: string; data: Record<string, unknown> }): void {
    this._floorPlan.applyPresentationFromPublicShape(shape);
  }

  /** Delegado en {@link UppKonvaFloorPlanModel.applyLabelsFromPublicShape}. */
  applyLabelsFromPublicShape(id: string, label: string, sublabel: string): void {
    this._floorPlan.applyLabelsFromPublicShape(id, label, sublabel);
  }

  exportModel(): string {
    return this._floorPlan.exportModel();
  }

  importModel(json: string): ReturnType<UppKonvaFloorPlanModel['importModel']> {
    return this._floorPlan.importModel(json);
  }

  exportToPng(options?: UppFloorPlanExportPngOptions): Promise<Blob | null> {
    return this._floorPlan.exportToPng(options);
  }

  /** English comment normalized. */
  konvaShapeBySceneUuid(uuid: string) {
    return konvaShapeByUuidFromModel(this._floorPlan, uuid);
  }

  /** Estado **`attention`** en el elemento de escena (**solo mesas** exponen `true`). */
  shapeAttention(elementUuid: string): boolean {
    const h = this.konvaShapeBySceneUuid(elementUuid);
    return h?.attention === true;
  }

  /**
   * English comment normalized.
   * `false` limpia el resaltado. En tipos que no son mesa, activar no hace nada.
   */
  setShapeAttention(elementUuid: string, enabled: boolean): void {
    const h = this.konvaShapeBySceneUuid(elementUuid.trim());
    h?.alert(enabled);
  }

  /** Tono y pulso activos, o **null** si no hay resaltado en esa fila (solo mesas). */
  shapeHighlightState(elementUuid: string): { tone: UppKonvaAttention; animated: boolean } | null {
    const h = this.konvaShapeBySceneUuid(elementUuid.trim());
    if (!h || h.type !== 'table' || !h.attention) {
      return null;
    }
    return { tone: h.attentionTone, animated: h.attentionAnimated };
  }

  /** English comment normalized. */
  setShapeHighlight(elementUuid: string, tone: UppKonvaAttention, animated: boolean): void {
    this.konvaShapeBySceneUuid(elementUuid.trim())?.setHighlight(tone, animated);
  }

  clearShapeHighlight(elementUuid: string): void {
    this.konvaShapeBySceneUuid(elementUuid.trim())?.clearHighlight();
  }

  /**
   * English comment normalized.
   */
  static floorPlanElementUuid(value: string): string {
    return floorPlanElementUuid(value);
  }
}

export { UppKonvaFloorPlanModel } from './internal/upp-konva-floor-plan-model';
export { mergeUppFloorPlanTheme, UPPKONVA_DARKTHEME, UPPKONVA_LIGHTTHEME } from './plan/floor-plan-theme';
export type { UppFloorPlanPreferences } from './plan/floor-plan-preferences';
export {
  adapterAreaToAreaSource,
  areaSourceToAdapterArea,
  floorPlanModelFromPublicAreaSource,
  uppKonvaAreaWire,
} from './plan/adapters/floor-plan-from-area-source';
/** English comment normalized. */
export type { UppKonvaArea as AdapterKonvaArea } from './plan/adapters/floor-plan-from-area-source';
export type { UppFloorPlanExportPngOptions } from './export/upp-floor-plan-export-png';
export {
  createPublicGroupForArea,
  installUppKonvaGroupedBridgeToArea,
} from './grouping/upp-konva-public-group-bridge';
